import React, { Children, cloneElement, isValidElement, useId } from 'react';

const CONTROL_TAGS = new Set(['input', 'select', 'textarea', 'button']);

const PROMPT_LABELS = {
  'введите адрес': 'Адрес',
  'начните вводить адрес': 'Адрес',
  'напишите комментарий': 'Комментарий',
  'подтвердите новый пароль': 'Подтверждение нового пароля',
  'повторите новый пароль': 'Подтверждение нового пароля',
  'укажите причину': 'Причина',
  'укажите причину отмены': 'Причина отмены',
  'выберите администратора': 'Администратор',
  'выберите дату': 'Дата',
  'выберите время': 'Время',
  'выберите группу': 'Группа',
  'выберите интервал': 'Интервал',
  'выберите исполнителя': 'Исполнитель',
  'выберите категорию': 'Категория',
  'выберите подразделение': 'Подразделение',
  'выберите поток': 'Поток',
  'выберите причину': 'Причина',
  'выберите сотрудника': 'Сотрудник',
  'выберите статус': 'Статус',
  'выберите тип': 'Тип',
  'выберите тип заказа': 'Тип заказа',
  'выберите тип операции': 'Тип операции',
  'выберите тип пользователя': 'Тип пользователя',
  'выберите тип услуги': 'Тип услуги',
  'выберите услугу': 'Услуга',
  'выберите этап': 'Этап'
};

export const normalizeFloatingLabel = (value, fallback = 'Поле') => {
  if (typeof value !== 'string') return value || fallback;
  const trimmed = value.trim();
  const promptKey = trimmed.toLowerCase().replace(/[.:…\s]+$/g, '');
  if (PROMPT_LABELS[promptKey]) return PROMPT_LABELS[promptKey];
  const normalized = trimmed
    .replace(/^(?:начните вводить|выберите|введите|укажите|напишите|подтвердите|повторите)\s*/i, '')
    .replace(/^[.:\s]+|[.:\s]+$/g, '');
  if (!normalized) return fallback;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const hasDisplayValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && String(value).length > 0;
};

const labelToText = (label) => Children.toArray(label)
  .map((part) => {
    if (typeof part === 'string' || typeof part === 'number') return String(part);
    if (!isValidElement(part)) return '';
    return labelToText(part.props.children);
  })
  .join('')
  .replace(/\s+/g, ' ')
  .trim();

const findControl = (node) => {
  if (!isValidElement(node)) return null;
  if (typeof node.type === 'string' && CONTROL_TAGS.has(node.type)) return node;

  let result = null;
  Children.forEach(node.props.children, (child) => {
    if (!result) result = findControl(child);
  });
  return result;
};

const enhanceFirstControl = (node, enhancement, state) => {
  if (!isValidElement(node) || state.enhanced) return node;

  if (typeof node.type === 'string' && CONTROL_TAGS.has(node.type)) {
    state.enhanced = true;
    const {
      placeholder: fallbackPlaceholder,
      ...controlEnhancement
    } = enhancement;
    return cloneElement(node, {
      ...controlEnhancement,
      className: `${node.props.className || ''} floating-field__control`.trim(),
      id: node.props.id || enhancement.id,
      placeholder: node.type === 'input' || node.type === 'textarea'
        ? (node.props.placeholder ?? fallbackPlaceholder)
        : node.props.placeholder,
      'aria-label': node.props['aria-label'] || enhancement['aria-label']
    });
  }

  if (!node.props.children) return node;
  return cloneElement(node, undefined, Children.map(
    node.props.children,
    (child) => enhanceFirstControl(child, enhancement, state)
  ));
};

/**
 * Единая рамка поля с плавающей подписью.
 * Принимает один input/select/textarea/button либо обертку с одним контролом внутри.
 */
const FloatingField = ({
  label,
  children,
  className = '',
  filled,
  active = false,
  disabled,
  error = false,
  alwaysFloat = false,
  showHint = false,
  initialHint = false,
  id,
  labelOffset,
  labelClassName = ''
}) => {
  const generatedId = useId().replace(/:/g, '');
  const control = findControl(children);
  const controlType = control?.props?.type;
  const controlValue = control?.props?.value ?? control?.props?.defaultValue;
  const isTelephone = controlType === 'tel';
  const isMultiline = control?.type === 'textarea';
  const hasTextControl = control?.type === 'input' || isMultiline;
  const shouldAlwaysFloat = alwaysFloat
    || showHint
    || isTelephone
    || control?.type === 'select'
    || ['date', 'datetime-local', 'month', 'time', 'week'].includes(controlType);
  const isFilled = filled === undefined ? hasDisplayValue(controlValue) : !!filled;
  const isDisabled = disabled === undefined ? !!control?.props?.disabled : !!disabled;
  const controlId = control?.props?.id || id || `floating-field-${generatedId}`;
  const plainLabel = labelToText(label);
  const enhancedChildren = enhanceFirstControl(children, {
    id: controlId,
    placeholder: plainLabel || ' ',
    'aria-label': plainLabel || undefined
  }, { enhanced: false });

  return (
    <div
      className={`floating-field ${className}`.trim()}
      style={labelOffset ? { '--floating-label-left': labelOffset } : undefined}
      data-active={active ? 'true' : 'false'}
      data-disabled={isDisabled ? 'true' : 'false'}
      data-error={error ? 'true' : 'false'}
      data-filled={isFilled ? 'true' : 'false'}
      data-float={shouldAlwaysFloat ? 'true' : 'false'}
      data-has-placeholder={hasTextControl ? 'true' : 'false'}
      data-initial-hint={initialHint || hasTextControl ? 'true' : 'false'}
      data-multiline={isMultiline ? 'true' : 'false'}
      data-show-hint={showHint || isTelephone ? 'true' : 'false'}
    >
      <fieldset className="floating-field__outline">
        <legend className="floating-field__legend" aria-hidden="true">
          <span>{label}</span>
        </legend>
        <div className="floating-field__content">{enhancedChildren}</div>
        <label htmlFor={controlId} className={`floating-field__label ${labelClassName}`.trim()}>
          {label}
        </label>
      </fieldset>
    </div>
  );
};

export default FloatingField;
